import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Wand2, Edit, X } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

interface HiddenPromptGeneratorProps {
  recipeId: string;
  initialPrompt: string;
  onGenerate: (prompt: string) => void;
  isGenerating: boolean;
}

export const HiddenPromptGenerator: React.FC<HiddenPromptGeneratorProps> = ({
  recipeId: _recipeId,
  initialPrompt,
  onGenerate,
  isGenerating
}) => {
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState(initialPrompt);

  const handleQuickGenerate = () => {
    onGenerate(initialPrompt);
  };

  const handleCustomGenerate = () => {
    onGenerate(editedPrompt);
  };

  const togglePromptVisibility = () => {
    setIsPromptVisible(!isPromptVisible);
    if (!isPromptVisible) {
      setEditedPrompt(initialPrompt); // Reset to original when opening
    }
  };

  const resetPrompt = () => {
    setEditedPrompt(initialPrompt);
  };

  return (
    <div className="my-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
      
      {/* Main Action Section */}
      <div className="space-y-3">
        
        {/* Primary Generate Button */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleQuickGenerate}
            disabled={isGenerating}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium"
            size="sm"
          >
            {isGenerating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            ) : (
              <Wand2 className="h-4 w-4 mr-2" />
            )}
            {isGenerating ? 'Generating...' : 'Generate Image'}
          </Button>
          
          {/* Compact Toggle Button - Always Available */}
          <Button
            onClick={togglePromptVisibility}
            variant="outline"
            size="sm"
            className="w-9 h-9 p-0 rounded-full border-blue-300 text-blue-600 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-800/30"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>

        {/* Status Message */}
        <div className="text-xs text-blue-600 dark:text-blue-400">
          {isGenerating ? (
            <span className="flex items-center">
              <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Creating your image... (~15-30 seconds)
            </span>
          ) : (
            <span>Ready to generate AI food photography</span>
          )}
        </div>

        {/* Expandable Prompt Editor - Clean, Full Width */}
        {isPromptVisible && (
          <div className="border-t border-blue-200 dark:border-blue-700 pt-3 space-y-3">
            
            {/* Prompt Label */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                AI Image Prompt
              </span>
              <Button
                onClick={() => setIsPromptVisible(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            
            {/* Clean, Wide Text Area */}
            <div className="space-y-2">
              <Textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                className="min-h-[200px] text-sm resize-none border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500"
                placeholder="Describe the image you want to generate..."
              />
              
              {/* Character Count */}
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>{editedPrompt.length} characters</span>
                {editedPrompt !== initialPrompt && (
                  <Button
                    onClick={resetPrompt}
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-blue-600 hover:text-blue-700 p-1"
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleCustomGenerate}
                disabled={isGenerating || editedPrompt.trim().length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Generate with Custom Prompt
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HiddenPromptGenerator;